<template>
  <fragment>
    <mc-app-header :title="`Classes of ${displayName(users.selected)}`">
      <template #left>
        <div class="level-item">
          <b-taglist attached>
            <b-tag type="is-dark">Classes Remaining</b-tag>
            <b-tag type="is-info">
              {{ displayNumClassesRemaining(users.selected) }}
            </b-tag>
          </b-taglist>
        </div>
      </template>
    </mc-app-header>
    <mc-app-body>
      <div class="classes section">
        <mc-split-view container-class="criteria" height="100%">
          <template #leftHeader>
            <div class="header-title">
              Classes
            </div>
          </template>
          <template #leftBody>
            <div class="mc-container">
              <div class="classes__wrapper">
                <div
                  class="box classes__form"
                  @click="
                    newClass.challenge &&
                      selectChallengeId(newClass.challenge.id)
                  "
                >
                  <b-field>
                    <mc-challenge-select
                      v-model="newClass.challenge"
                      class="classes__form__challenge-select"
                      :exclude-if="isPastClass"
                    />
                    <b-button
                      class="button is-primary"
                      @click="createClassHandler"
                    >
                      Schedule
                    </b-button>
                  </b-field>
                </div>
              </div>
              <div class="classes__wrapper">
                <mc-paginated-list resource-state="classes">
                  <div v-if="classes.list.length === 0">
                    <div class="content has-text-centered">
                      There are no previously completed classes!
                    </div>
                  </div>
                  <div v-else>
                    <div
                      v-for="session in classes.list"
                      :key="session.id"
                      :class="[
                        'classes__item',
                        'box',
                        {
                          'classes__item--active':
                            session.challenge.id == selectedChallengeId
                        }
                      ]"
                      @click="selectChallengeId(session.challenge.id)"
                    >
                      <div class="classes__item-details">
                        <div class="classes__item-title">
                          {{ session.challenge.name }}
                        </div>
                        <b-taglist>
                          <b-tag type="is-info">
                            {{ displayTimeFromTimestamp(session.scheduledAt) }}
                          </b-tag>
                          <b-tag
                            :type="session.status ? 'is-success' : 'is-warning'"
                          >
                            {{
                              session.status != null
                                ? session.status
                                : 'Yet to start!'
                            }}
                          </b-tag>
                        </b-taglist>
                      </div>
                      <div class="buttons">
                        <b-button
                          v-if="session.status == null"
                          class="button is-link is-small"
                          @click.prevent="updateClassHandler(session)"
                        >
                          Reschedule
                        </b-button>
                        <router-link
                          v-else-if="session.status == 'completed'"
                          class="button is-link is-small"
                          :to="
                            `/challenge_attempts/${
                              session.challenge.id
                            }_${userId}/review`
                          "
                        >
                          Attempt
                        </router-link>
                        <b-button
                          :disabled="classes.deleting"
                          class="button is-danger is-small"
                          icon-right="delete"
                          @click.prevent="deleteClassHandler(session)"
                        />
                      </div>
                    </div>
                  </div>
                </mc-paginated-list>
              </div>
            </div>
          </template>
          <template #rightHeader>
            Related Concept Progress
          </template>
          <template #rightBody>
            <related-concepts-progress />
          </template>
        </mc-split-view>
      </div>
    </mc-app-body>
  </fragment>
</template>
<script>
import { mapState, mapActions } from 'vuex';

import { createClass, updateClass } from './SaveClassModal';
import { displayTimeFromTimestamp } from '../../../utils/time';
import { displayName } from '../../../utils/user';
import RelatedConceptsProgress from './RelatedConceptsProgress';

export default {
  data() {
    return {
      newClass: {
        challenge: null
      },
      selectedChallengeId: null
    };
  },
  created() {
    this.load();
  },
  computed: {
    ...mapState(['classes', 'users']),
    userId() {
      return this.$route.params.id;
    },
    pastChallengeIds() {
      return this.classes.list.map(c => c.challenge.id);
    }
  },
  watch: {
    ['newClass.challenge'](newVal) {
      this.selectChallengeId(newVal.id);
    }
  },
  methods: {
    ...mapActions('users', ['selectUser']),
    ...mapActions('classes', ['setFilter', 'deleteClass']),
    ...mapActions('relatedConcepts', ['loadChallengeId', 'clearChallengeId']),
    displayName,
    displayNumClassesRemaining(user) {
      return user.numClassesRemaining > 999
        ? 'Unlimited'
        : user.numClassesRemaining;
    },
    async createClassHandler() {
      if (!this.newClass.challenge) {
        this.$buefy.toast.open({
          message: 'Please select a challenge first!',
          type: 'is-danger'
        });
        return;
      }
      try {
        await createClass(this, this.userId, this.newClass.challenge.id);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Class creation canceled!',
          type: 'is-danger'
        });
      }
    },
    async updateClassHandler(classToUpdate = this.scheduledClass) {
      try {
        await updateClass(this, classToUpdate);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Class update canceled!',
          type: 'is-danger'
        });
      }
    },
    async deleteClassHandler(classToDelete) {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want delete this `class`?',
        confirmText: 'Delete Class!',
        type: 'is-danger',
        onConfirm: async () => {
          try {
            await this.deleteClass(classToDelete.id);
            this.$buefy.toast.open({
              message: 'Class Deleted!',
              type: 'is-success'
            });
          } catch (error) {
            this.$buefy.toast.open({
              message: `Class deleting failed: ${error}`,
              type: 'is-danger'
            });
          }
        }
      });
    },
    isPastClass(challengeId) {
      return this.pastChallengeIds.indexOf(challengeId) != -1;
    },
    selectChallengeId(id) {
      if (this.selectedChallengeId !== id) {
        this.selectedChallengeId = id;
        this.loadChallengeId({ challengeId: id, userId: this.userId });
      }
    },
    load() {
      this.setFilter({
        user: `/users/${this.userId}`
      });
      this.selectUser(this.userId);
      this.clearChallengeId();
    },
    displayTimeFromTimestamp
  },
  components: {
    RelatedConceptsProgress
  }
};
</script>

<style lang="scss" scoped>
.classes {
  height: 100%;
  &__wrapper {
    margin: 0.4rem;
  }

  &__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 1rem;
    margin-bottom: 0.5rem;
    cursor: pointer;

    &:hover {
      background-color: rgba($secondary-color, 0.5);
    }

    &--active {
      background-color: $secondary-color !important;
    }
  }

  &__item-details {
    flex: 1 0 0;
    flex-direction: column;
    margin-right: 0.5rem;
  }

  &__item-title {
    margin-bottom: 0.5rem;
    &--spacing {
      margin-bottom: 0.5rem;
    }
  }

  &__form {
    margin-bottom: 0.5rem;
    &__challenge-select {
      flex-grow: 1;
      padding-right: 0.2rem;
    }
  }
}
</style>
